import {
  useMediaCategoryControllerClearMediaCategoryMutation,
  useMediaCategoryControllerGetMediaCategoryQuery,
  useMediaCategoryControllerSetMediaCategoryMutation,
} from "~/ui-rtk/api/mediaCategoryApi.ts"
import useSources from "~/ui-rtk/hooks/sources"
import { useStats } from "../hooks/stats"
import { TCampaignAdSetAdDetailsProps } from "../constants"
import { ChangeEvent, useEffect, useMemo, useState } from "react"
import { useAdSetQubeQuery } from "./hooks/cube-query"
import { H5, H6 } from "~/ui-rtk/components/ui/typography"
import { Card, Loader } from "~/ui-rtk/components/ui/common"
import { renderSourceIcon } from "~/ui-rtk/components/ui/charts/TableVisualization/components/Cell/utils"
import { TSource } from "~/ui-rtk/constants/table-visualization-types"
import BrandValueOverSpendProgressBar from "../components/BrandValueOverSpendProgressBar"
import { Select } from "~/ui-rtk/components/ui/controls"
import StatsTable from "../components/StatsTable"
import { renderDetailsCaption } from "../utils"
import { DetailsTitle } from "../components/DetailsHeader"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { URL_ADSET_ID_PARAM_KEY } from "~/ui-rtk/constants/url-params"
import { URL_WIDGET_PARAMS } from "../../dashboards/BrandMediaOptimizationDashboard/constants"
import { ArrowTopRightOnSquare } from "~/ui-rtk/components/ui/svg/arrow/ArrowTopRightOnSquare"
import NavigationButton from "~/ui-rtk/components/ui/controls/NavigationButton/NavigationButton"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import { InitiativeType } from "~/ui-rtk/api/types/custom-attribution-enums"
import { INITIATIVE_TYPE_LABELS } from "~/ui-rtk/constants/initiative-types"
import { InitiativeTypeDropdown } from "../../initiative-type"
import {
  useCustomAttributionControllerGetAllMediaTargetsQuery,
  useCustomAttributionControllerGetInitiativeTypesQuery,
} from "~/ui-rtk/api/customAttributionApi"

export enum COMPARE_AD_SET_MODE {
  ALL,
  CURRENT_CAMPAIGN,
}
export type TDataKey = "ALL" | "CURRENT_CAMPAIGN"
export const COMPARE_AD_SET_KEY_MAP: Record<COMPARE_AD_SET_MODE, TDataKey> = {
  [COMPARE_AD_SET_MODE.ALL]: "ALL",
  [COMPARE_AD_SET_MODE.CURRENT_CAMPAIGN]: "CURRENT_CAMPAIGN",
}
export const COMPARE_AD_SET_LABEL_MAP: Record<COMPARE_AD_SET_MODE, string> = {
  [COMPARE_AD_SET_MODE.ALL]: "All",
  [COMPARE_AD_SET_MODE.CURRENT_CAMPAIGN]: "Current Campaign",
}

export default function AdSetDetails(props: TCampaignAdSetAdDetailsProps) {
  const { sources } = useSources()
  const { metric, viewAdsUrl } = props
  const stats = useStats(metric)
  const [category, setCategory] = useState<string | null | undefined>(undefined)
  const campaignName = metric.campaign_name as string
  const campaignId = metric.campaign_id as string
  const adSetName = metric.adset_name as string
  const adSetId = metric.adset_id as string
  const sourceName = (metric.media_channel as string)?.toUpperCase()
  const source = sources.get(sourceName)

  const { isEnabled: isDemoEnabled } = useDemoMode()

  const [setMediaCategory] =
    useMediaCategoryControllerSetMediaCategoryMutation()
  const [clearMediaCategory] =
    useMediaCategoryControllerClearMediaCategoryMutation()

  const { data: allMediaTargets, refetch: refetchMediaTargets } =
    useCustomAttributionControllerGetAllMediaTargetsQuery()

  const { data: detailedTypes = [] } =
    useCustomAttributionControllerGetInitiativeTypesQuery()

  const existingMediaTarget = (
    allMediaTargets as Record<string, unknown>[] | undefined
  )?.find(target => target.campaign_id === campaignId)
  const {
    data: mediaCategory,
    isLoading: mediaCategoryLoading,
    isFetching: mediaCategoryFetching,
    refetch,
  } = useMediaCategoryControllerGetMediaCategoryQuery(
    {
      id: adSetId,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  useEffect(() => {
    if ((existingMediaTarget as Record<string, unknown>)?.initiativeType) {
      setCategory(
        (existingMediaTarget as Record<string, unknown>)
          .initiativeType as string,
      )
    } else if (mediaCategory?.category) {
      setCategory(mediaCategory.category)
    } else {
      setCategory(null)
    }
  }, [existingMediaTarget, mediaCategory])

  const categoryUpdate = async (value: string) => {
    if (!value) {
      // Clear selection - use backend endpoint to handle all cleanup
      await clearMediaCategory({ type: "adset", id: campaignId })
      setCategory(null)
      await refetch()
      await refetchMediaTargets()
      return
    }

    // Set category/initiative type - let backend handle all the logic
    await setMediaCategory({
      type: "adset",
      id: campaignId,
      body: {
        value,
        channel: metric.media_channel as string,
      },
    })

    setCategory(value)
    await refetch()
    await refetchMediaTargets()
  }
  const [compareAdSetMode, setCompareAdSetMode] = useState(
    COMPARE_AD_SET_KEY_MAP[COMPARE_AD_SET_MODE.ALL],
  )

  const { allAdSetsDetails, isLoading } = useAdSetQubeQuery(
    compareAdSetMode ===
      COMPARE_AD_SET_KEY_MAP[COMPARE_AD_SET_MODE.CURRENT_CAMPAIGN]
      ? campaignId
      : undefined,
  )

  const compareAdSetModeOptions = useMemo(
    () => [
      {
        value: COMPARE_AD_SET_KEY_MAP[COMPARE_AD_SET_MODE.ALL],
        label: COMPARE_AD_SET_LABEL_MAP[COMPARE_AD_SET_MODE.ALL],
      },
      {
        value: COMPARE_AD_SET_KEY_MAP[COMPARE_AD_SET_MODE.CURRENT_CAMPAIGN],
        label: COMPARE_AD_SET_LABEL_MAP[COMPARE_AD_SET_MODE.CURRENT_CAMPAIGN],
      },
    ],
    [],
  )

  const viewChildrenAdditionalUrlParams = useMemo(
    () => `${URL_ADSET_ID_PARAM_KEY}=${adSetId}`,
    [campaignId],
  )
  const combinedOptions = [
    { value: "", label: "Clear Selection" },
    // Media category options (these will use the media category system)
    {
      value: "Exclude",
      label: "Exclude",
    },
    // Media target options (these will use the media target system)
    {
      value: InitiativeType.DEMAND_CAPTURE_ACQUISITION,
      label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_CAPTURE_ACQUISITION],
    },
    {
      value: InitiativeType.DEMAND_CAPTURE_RETARGETING,
      label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_CAPTURE_RETARGETING],
    },
    {
      value: InitiativeType.DEMAND_CAPTURE_BROAD,
      label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_CAPTURE_BROAD],
    },
    {
      value: InitiativeType.DEMAND_GENERATION,
      label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_GENERATION],
    },
    {
      value: InitiativeType.DEMAND_GENERATION_2,
      label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_GENERATION_2],
    },
    {
      value: InitiativeType.RETENTION,
      label: INITIATIVE_TYPE_LABELS[InitiativeType.RETENTION],
    },
    ...detailedTypes
      .filter(t => t.isCustom)
      .map(t => ({ value: t.value, label: t.label })),
  ]
  return (
    <div className="campaign-details w-full h-full">
      <DetailsTitle
        title={adSetName ?? ""}
        classes={{ container: "mb-4" }}
        demoMode={isDemoEnabled}
      />
      {isLoading && (
        <div className="h-[50%] w-full flex items-center justify-center">
          <Loader />
        </div>
      )}
      {!isLoading && (
        <>
          <div className="mt-3">
            {mediaCategoryLoading || mediaCategoryFetching ? (
              <Loader />
            ) : (
              <InitiativeTypeDropdown
                value={category || ""}
                options={combinedOptions}
                onChange={categoryUpdate}
                onStopPropagation={e => e.stopPropagation()}
                placeholder="Select Initiative Type or Category..."
              />
            )}
          </div>
          <div className="flex justify-between items-center mt-3">
            Compare against:
            <Select
              className="w-[50%] bg-basic-dark-blue"
              options={compareAdSetModeOptions}
              value={compareAdSetMode}
              onChange={($evt: ChangeEvent<HTMLSelectElement>) => {
                setCompareAdSetMode(
                  ($evt.target as HTMLSelectElement)
                    .value as (typeof COMPARE_AD_SET_KEY_MAP)[keyof typeof COMPARE_AD_SET_KEY_MAP],
                )
              }}
            />
          </div>
          <Card
            rounded
            className="mt-3 bg-basic-dark-blue border border-basic-blue"
          >
            <div className="grid grid-cols-8">
              <div className="col-span-7">
                <H6
                  className={cn("font-bold mb-2", isDemoEnabled && "blur-md")}
                >
                  {adSetName as React.ReactNode}
                </H6>
                <H6 className={cn("font-semibold", isDemoEnabled && "blur-md")}>
                  Campaign: {campaignName as React.ReactNode}
                </H6>
              </div>
              <div className="flex items-center justify-end">
                {renderSourceIcon({
                  name: source?.name,
                  icon: source?.icon,
                } as TSource)}
              </div>
            </div>
            <div className="mt-3 text-sm">
              {renderDetailsCaption(
                metric,
                (allAdSetsDetails as Record<string, unknown>) ??
                  ({} as Record<string, unknown>),
                "ad set",
                sourceName.toLowerCase() as "meta" | "tiktok",
              )}
            </div>
            <BrandValueOverSpendProgressBar
              metric={metric}
              allDetails={
                (allAdSetsDetails as Record<string, unknown>) ??
                ({} as Record<string, unknown>)
              }
              label="ad set"
              withinCurrent={
                compareAdSetMode ===
                COMPARE_AD_SET_KEY_MAP[COMPARE_AD_SET_MODE.CURRENT_CAMPAIGN]
                  ? "campaign"
                  : undefined
              }
            />
          </Card>
          <div className="mt-3">
            <NavigationButton
              to={
                viewAdsUrl ||
                `?widget=${URL_WIDGET_PARAMS.AD}&${viewChildrenAdditionalUrlParams}`
              }
            >
              <ArrowTopRightOnSquare fill="white" size={20} /> View Ads
            </NavigationButton>
          </div>
          <Card
            rounded
            className="mt-3 bg-basic-dark-blue border border-basic-blue"
          >
            <H5 className="font-bold">Stats</H5>
            <StatsTable {...stats} />
          </Card>
        </>
      )}
    </div>
  )
}
